import { ComponentChildren } from 'preact';
import { Text as T } from '@eo-locale/preact';
import clsx from 'clsx';
import { FunctionComponent } from 'preact/compat';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { useToggle } from 'utilities/use-toggle';
import { useUniqueId } from 'utilities/use-unique-id';

import { Labelled } from 'components/Labelled';
import { Text } from 'components/Text';

import { FileInput } from './components/FileInput';
import { FileUpload } from './components/FileUpload';
import { fileAccepted, getDataTransferFiles } from './utilities';

import styles from './DropZone.module.scss';

interface DropZoneProps {
  /** Label for the dropzone */
  label: string;
  /** Hide label visually */
  labelHidden?: boolean;
  /** Additional content to display beneath the dropzone */
  helpText?: string;
  /** Error message */
  error?: string;
  /** Sets a disabled state */
  disabled?: boolean;
  /** Comma-separated list of supported MIME-types */
  accept?: string;
  /** Allow upload of multiple files
   * @default true
   */
  multiple?: boolean;
  /** Sets file dialog state */
  openFileDialog?: boolean;
  uploaded?: boolean;
  /** The child elements to be rendered in the dropzone */
  children?: ComponentChildren;
  /** Callback triggered on file dialog close */
  onFileDialogClose?: () => void;
  /** Callback triggered on files drop */
  onDrop?: (
    files: File[],
    acceptedFiles: File[],
    rejectedFiles: File[],
  ) => void;
}

export const DropZone: FunctionComponent<DropZoneProps> & {
  FileUpload: typeof FileUpload;
} = function DropZone({
  label,
  labelHidden,
  helpText,
  error,
  disabled,
  accept,
  multiple = true,
  openFileDialog,
  uploaded,
  children,
  onFileDialogClose,
  onDrop,
}: DropZoneProps) {
  const id = useUniqueId('DropZone');
  const dropZoneNode = useRef<HTMLDivElement>(null);
  const dragTargets = useRef<EventTarget[]>([]);
  const nodeWidth = dropZoneNode.current?.getBoundingClientRect().width;

  const [dragging, setDragging] = useState(false);
  const [internalError, setInternalError] = useState(false);
  const {
    value: focused,
    setTrue: handleFocus,
    setFalse: handleBlur,
  } = useToggle(false);

  let className = null;

  if (uploaded)
    className = clsx(
      styles.DropZone,
      styles.hasImages,
      dragging && styles.isDragging,
      disabled && styles.isDisabled,
      (error || internalError) && styles.hasError,
    );
  else
    className = clsx(
      styles.DropZone,
      dragging && styles.isDragging,
      disabled && styles.isDisabled,
      focused && styles.focused,
      (error || internalError) && styles.hasError,
    );

  const open = useCallback(() => {
    const fileInputNode =
      dropZoneNode.current && dropZoneNode.current.querySelector(`#${id}`);

    fileInputNode &&
      fileInputNode instanceof HTMLElement &&
      fileInputNode.click();
  }, [id]);

  const handleClick = useCallback(() => {
    !disabled && open();
  }, [disabled, open]);

  const getValidatedFiles = useCallback(
    (files: File[] | DataTransferItem[]) => {
      const acceptedFiles: File[] = [];
      const rejectedFiles: File[] = [];

      Array.from(files as File[]).forEach((file: File) => {
        !fileAccepted(file, accept)
          ? rejectedFiles.push(file)
          : acceptedFiles.push(file);
      });

      if (!multiple) {
        acceptedFiles.splice(1, acceptedFiles.length);
        rejectedFiles.push(...acceptedFiles.slice(1));
      }

      return { files, acceptedFiles, rejectedFiles };
    },
    [accept, multiple],
  );

  const handleDragEnter = useCallback(
    (event: DragEvent) => {
      stopEvent(event);

      if (disabled) {
        return;
      }

      const fileList = getDataTransferFiles(event);

      if (event.target && !dragTargets.current.includes(event.target)) {
        dragTargets.current.push(event.target);
      }

      if (dragging) return;

      const { rejectedFiles } = getValidatedFiles(fileList);

      setDragging(true);
      setInternalError(rejectedFiles.length > 0);
    },
    [disabled, dragging, getValidatedFiles],
  );

  const handleDragLeave = useCallback(
    (event: DragEvent) => {
      event.preventDefault();

      if (disabled) {
        return;
      }

      dragTargets.current = dragTargets.current.filter((el) => {
        const node = dropZoneNode.current;

        return el !== event.target && node && node.contains(el as HTMLElement);
      });

      if (dragTargets.current.length > 0) return;

      setDragging(false);
      setInternalError(false);
    },
    [disabled],
  );

  const handleDrop = useCallback(
    (event: DragEvent) => {
      stopEvent(event);

      if (disabled) {
        return;
      }

      const fileList = getDataTransferFiles(event);

      const { files, acceptedFiles, rejectedFiles } =
        getValidatedFiles(fileList);

      dragTargets.current = [];

      setDragging(false);
      setInternalError(rejectedFiles.length > 0);

      onDrop && onDrop(files as File[], acceptedFiles, rejectedFiles);

      (event.target as HTMLInputElement).value = '';
    },
    [disabled, getValidatedFiles, onDrop],
  );

  const handleDragOver = useCallback((event: DragEvent) => {
    stopEvent(event);
  }, []);

  useEffect(() => {
    const target = dropZoneNode.current;

    if (!target) {
      return;
    }

    target.addEventListener('drop', handleDrop);
    target.addEventListener('dragenter', handleDragEnter);
    target.addEventListener('dragleave', handleDragLeave);
    target.addEventListener('dragover', handleDragOver);

    return () => {
      target.removeEventListener('drop', handleDrop);
      target.removeEventListener('dragenter', handleDragEnter);
      target.removeEventListener('dragleave', handleDragLeave);
      target.removeEventListener('dragover', handleDragOver);
    };
  }, [handleDrop, handleDragEnter, handleDragLeave, handleDragOver]);

  const overlayMarkup =
    dragging && !internalError && !error ? (
      <div className={styles.Overlay}>
        <Text size="small" color="interactive">
          <T
            id="write_review_add_images_drop_message"
            defaultMessage="Drop files to upload"
          />
        </Text>
      </div>
    ) : null;

  const errorOverlayMarkup =
    dragging && internalError ? (
      <div className={clsx(styles.Overlay, styles['Overlay--critical'])}>
        {nodeWidth && nodeWidth > 250 && (
          <Text size="small" color="critical">
            <T
              id="write_review_add_images_type_invalid_message"
              defaultMessage="File type is not valid"
            />
          </Text>
        )}
      </div>
    ) : null;

  const fileInputProps = {
    id,
    multiple,
    open: openFileDialog,
    accept,
    onFocus: handleFocus,
    onBlur: handleBlur,
  };

  return (
    <Labelled
      id={id}
      label={label}
      labelHidden={labelHidden}
      helpText={helpText}
      error={error}
    >
      <div
        ref={dropZoneNode}
        className={className}
        aria-disabled={disabled}
        onClick={handleClick}
        onDragStart={stopEvent}
      >
        {overlayMarkup}
        {errorOverlayMarkup}
        <FileInput
          {...fileInputProps}
          onClose={onFileDialogClose}
          onChange={handleDrop as any}
        />
        <div className={styles.Container}>{children}</div>
      </div>
    </Labelled>
  );
};

DropZone.FileUpload = FileUpload;

function stopEvent(event: DragEvent) {
  event.preventDefault();
  event.stopPropagation();
}
